/**
 * Toast Component
 * 
 * Displays temporary notification messages with different severity levels.
 * Used for error messages, success confirmations, and informational alerts.
 * 
 * Requirements: 7.4, 7.5, 9.3
 */

import { useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  /** Unique identifier for this toast */
  id: string;
  /** Type of toast (determines styling and icon) */
  type: ToastType;
  /** Message to display */
  message: string;
  /** Optional duration in milliseconds (default: 3000) */
  duration?: number;
  /** Callback when toast is dismissed */
  onDismiss: (id: string) => void;
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Toast component - Individual notification message
 */
export function Toast({
  id,
  type,
  message,
  duration = 3000,
  onDismiss,
  action,
}: ToastProps) {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, onDismiss]);

  const handleDismiss = () => {
    onDismiss(id);
  };

  const handleAction = () => {
    action?.onClick();
    onDismiss(id);
  };

  // Icon and styling based on type
  const config = {
    success: {
      icon: '✓',
      bgColor: 'bg-green-500',
      textColor: 'text-white',
      borderColor: 'border-green-600',
    },
    error: {
      icon: '✕',
      bgColor: 'bg-red-500',
      textColor: 'text-white',
      borderColor: 'border-red-600',
    },
    warning: {
      icon: '⚠',
      bgColor: 'bg-yellow-500',
      textColor: 'text-white',
      borderColor: 'border-yellow-600',
    },
    info: {
      icon: 'ℹ',
      bgColor: 'bg-blue-500',
      textColor: 'text-white',
      borderColor: 'border-blue-600',
    },
  };

  const { icon, bgColor, textColor, borderColor } = config[type];

  return (
    <div
      className={`
        ${bgColor} ${textColor} ${borderColor}
        border-l-4
        rounded-lg
        shadow-lg
        p-4
        mb-3
        min-w-[300px]
        max-w-md
        animate-slide-in
      `}
      data-testid={`toast-${id}`}
      data-toast-type={type}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center font-bold text-lg">
          {icon}
        </div>

        {/* Message */}
        <div className="flex-1 pt-0.5">
          <p className="text-sm font-medium">{message}</p>
        </div>

        {/* Action button */}
        {action && (
          <button
            onClick={handleAction}
            className="flex-shrink-0 px-3 py-1 text-xs font-semibold bg-white bg-opacity-20 hover:bg-opacity-30 rounded transition-colors"
            data-testid={`toast-action-${id}`}
          >
            {action.label}
          </button>
        )}

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center hover:bg-white hover:bg-opacity-20 rounded transition-colors"
          aria-label="Dismiss notification"
          data-testid={`toast-dismiss-${id}`}
        >
          ×
        </button>
      </div>
    </div>
  );
}
